import { getUnsyncedPayloads, type createExamSessionStore } from "./store";
import type {
  SyncAdapter,
  PlatformAdapter,
  SubmissionType,
  SaveAnswerPayload,
} from "./types";

const SYNC_INTERVAL_MS = 30_000;
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 2000;
const MAX_DELAY_MS = 30_000;
interface StartExamEntry {
  timerId: ReturnType<typeof setTimeout> | null;
  promise: Promise<void> | null;
  done: boolean;
  error?: unknown;
  waiters: Array<() => void>;
}

export interface SubmitOpts {
  reason: SubmissionType;
  submittedAt: string;
  examEndTime?: string;
}

const startExamState = new Map<string, StartExamEntry>();
const activeSubmits = new Map<string, Promise<void>>();

export class NetworkUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NetworkUnavailableError";
  }
}

export class ExamAlreadySubmittedError extends Error {
  constructor() {
    super("Exam already submitted");
    this.name = "ExamAlreadySubmittedError";
  }
}

function isAlreadySubmittedError(err: unknown): boolean {
  const e = err as
    | { message?: string; response?: { data?: { detail?: string } } }
    | undefined;
  const detail = e?.response?.data?.detail ?? "";
  const message = e?.message ?? "";
  return (
    /exam already submitted/i.test(detail) ||
    /exam already submitted/i.test(message)
  );
}

function getErrorDetail(err: unknown): string {
  const e = err as
    | { message?: string; response?: { data?: { detail?: string } } }
    | undefined;
  return e?.response?.data?.detail ?? e?.message ?? "";
}

function isAttemptOverlapError(err: unknown): boolean {
  return /attempt overlap detected/i.test(getErrorDetail(err));
}

function getStartEntry(examId: string): StartExamEntry {
  let entry = startExamState.get(examId);
  if (!entry) {
    entry = { timerId: null, promise: null, done: false, waiters: [] };
    startExamState.set(examId, entry);
  }
  return entry;
}

export function markStartExamDone(examId: string): void {
  const entry = getStartEntry(examId);
  entry.done = true;
  entry.waiters.forEach((r) => r());
  entry.waiters = [];
}

export function scheduleStartExam(
  examId: string,
  fire: () => Promise<void>,
): void {
  const entry = getStartEntry(examId);
  if (entry.done || entry.timerId || entry.promise) return;

  entry.timerId = setTimeout(() => {
    entry.timerId = null;
    entry.promise = fire()
      .then(() => {
        entry.done = true;
      })
      .catch((err) => {
        entry.done = true;
        entry.error = err;
      });
    entry.waiters.forEach((r) => r());
    entry.waiters = [];
  }, Math.random() * 30_000);
}

export async function awaitStartExam(examId: string): Promise<void> {
  const entry = startExamState.get(examId);
  if (!entry) return;
  if (entry.done) {
    if (entry.error) throw entry.error;
    return;
  }
  if (!entry.timerId && !entry.promise) return;
  if (!entry.promise) {
    await new Promise<void>((resolve) => {
      entry.waiters.push(resolve);
    });
  }
  if (entry.promise) await entry.promise;
  if (entry.error) throw entry.error;
}

export function getActiveSubmit(examId: string): Promise<void> | null {
  return activeSubmits.get(examId) ?? null;
}

export function cleanupExam(examId: string): void {
  if (activeSubmits.has(examId)) return;
  const entry = startExamState.get(examId);
  if (entry?.timerId) clearTimeout(entry.timerId);
  startExamState.delete(examId);
}

export type StoreHandle = ReturnType<typeof createExamSessionStore>;

export class SyncEngine {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private retryCount = 0;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private isSyncing = false;
  private isRemotelySubmitted = false;

  constructor(
    private store: StoreHandle,
    private syncAdapter: SyncAdapter,
    private platformAdapter: PlatformAdapter,
  ) {}

  start() {
    this.stop();
    this.intervalId = setInterval(() => {
      this.resetBackoff();
      this.syncNow();
    }, SYNC_INTERVAL_MS);
  }

  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.resetBackoff();
  }

  resetBackoff() {
    this.retryCount = 0;
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }

  async syncNow(): Promise<void> {
    const state = this.store.getState();
    if (
      !this.platformAdapter.isOnline() ||
      this.isSyncing ||
      this.isRemotelySubmitted ||
      state.pendingEvents.length === 0
    )
      return;

    const payloads = getUnsyncedPayloads(state.pendingEvents);
    if (payloads.length === 0) return;

    this.isSyncing = true;

    try {
      try {
        await awaitStartExam(state.examId);
      } catch (err) {
        this.scheduleRetry();
        return;
      }

      let madeProgress = false;
      let skipScheduleRetry = false;

      try {
        await this.syncAdapter.saveAnswers(
          state.studentId,
          state.examId,
          payloads,
        );
        this.store.getState().markSynced(payloads.length);
        madeProgress = true;
        this.retryCount = 0;
      } catch (batchErr) {
        if (payloads.length === 0) {
          console.error(
            "[SyncEngine] saveAnswers failed with no payloads. Not retrying.",
            batchErr,
          );
          return;
        }

        if (isAttemptOverlapError(batchErr)) {
          console.warn(
            "[SyncEngine] Batch overlap detected. Saving individually once.",
          );
          const syncedQuestionIds = await this.saveIndividuallyOnce(
            state.studentId,
            state.examId,
            payloads,
          );
          this.store.getState().markSyncedQuestionIds(syncedQuestionIds);
          skipScheduleRetry = true;
          if (syncedQuestionIds.length > 0) {
            this.retryCount = 0;
          }
        } else {
          console.error(
            "[SyncEngine] Batch save failed. Will retry batch.",
            batchErr,
          );
        }
      }

      const postState = this.store.getState();
      if (skipScheduleRetry) {
        if (postState.pendingEvents.length === 0) {
          this.retryCount = 0;
        }
      } else if (postState.pendingEvents.length > 0) {
        if (madeProgress) {
          this.retryCount = 0;
        }
        this.scheduleRetry();
      } else {
        this.retryCount = 0;
      }
    } catch (err) {
      if (isAlreadySubmittedError(err)) {
        this.isRemotelySubmitted = true;
        this.store.getState().markSynced(payloads.length);
        return;
      }
      const message = err instanceof Error ? err.message : "Sync failed";
      console.error("[SyncEngine]", message);
      this.scheduleRetry();
    } finally {
      this.isSyncing = false;
    }
  }

  async syncAll(): Promise<void> {
    this.resetBackoff();
    for (let attempt = 0; attempt < 3; attempt++) {
      await this.syncNow();
      const remaining = getUnsyncedPayloads(
        this.store.getState().pendingEvents,
      );
      if (remaining.length === 0) return;
      await new Promise<void>((r) => setTimeout(r, 1000));
    }
    const unsaved = getUnsyncedPayloads(this.store.getState().pendingEvents);
    if (unsaved.length > 0) {
      console.warn(
        `[SyncEngine] ${unsaved.length} answer(s) could not be synced before submission`,
      );
    }
  }

  // Order: awaitStartExam → flush + sync → 25-30s delay → submitExam API → clear
  async runSubmit(examId: string, opts: SubmitOpts): Promise<void> {
    const existing = activeSubmits.get(examId);
    if (existing) return existing;

    // Mark submitting in the session store (persisted to localStorage for tab-close recovery)
    const state = this.store.getState();
    state.markSubmitting(opts.reason, opts.submittedAt, opts.examEndTime);
    this.store.flushPersist();

    const p = this.executeSubmit(examId, opts);
    activeSubmits.set(examId, p);
    return p;
  }

  async resumeSubmit(opts: SubmitOpts): Promise<void> {
    const examId = this.store.getState().examId;
    const existing = activeSubmits.get(examId);
    if (existing) return existing;

    const p = this.executeSubmit(examId, opts, true);
    activeSubmits.set(examId, p);
    return p;
  }

  private async executeSubmit(
    examId: string,
    opts: SubmitOpts,
    skipWaits = false,
  ): Promise<void> {
    try {
      this.store.flushPersist();
      await this.syncAll();

      if (!skipWaits) {
        await awaitStartExam(examId);
        await new Promise<void>((r) =>
          setTimeout(r, Math.random() * 5000 + 25000),
        );
      }

      if (!this.platformAdapter.isOnline()) {
        throw new NetworkUnavailableError(
          "device offline at submit time; deferred until reconnection",
        );
      }

      if (!this.isRemotelySubmitted) {
        const { studentId } = this.store.getState();
        try {
          await this.syncAdapter.submitExam(
            examId,
            studentId,
            opts.reason,
            opts.examEndTime,
            opts.submittedAt,
          );
        } catch (err) {
          if (!isAlreadySubmittedError(err)) {
            this.store.getState().clearSubmitting();
            throw err;
          }
          this.isRemotelySubmitted = true;
        }
      }

      await this.store.clearStorage();

      if (this.isRemotelySubmitted) {
        throw new ExamAlreadySubmittedError();
      }
    } finally {
      activeSubmits.delete(examId);
      startExamState.delete(examId);
    }
  }

  private async saveIndividuallyOnce(
    studentId: string,
    examId: string,
    payloads: SaveAnswerPayload[],
  ): Promise<string[]> {
    const syncedQuestionIds: string[] = [];
    for (const payload of payloads) {
      try {
        await this.syncAdapter.saveAnswers(studentId, examId, [payload]);
        syncedQuestionIds.push(payload.question_id);
      } catch (indivErr) {
        if (isAttemptOverlapError(indivErr)) {
          console.error(
            `[SyncEngine] Overlap for question ${payload.question_id}. Removing from pendingEvents.`,
            indivErr,
          );
          syncedQuestionIds.push(payload.question_id);
        } else {
          console.error(
            `[SyncEngine] Individual save failed for question ${payload.question_id}. Will retry on next sync interval.`,
            indivErr,
          );
        }
      }
    }
    return syncedQuestionIds;
  }

  private scheduleRetry() {
    if (this.isRemotelySubmitted) return;
    if (this.retryCount >= MAX_RETRIES) return;
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    const delay = Math.min(
      BASE_DELAY_MS * Math.pow(2, this.retryCount),
      MAX_DELAY_MS,
    );
    this.retryCount++;
    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      this.syncNow();
    }, delay);
  }
}
